import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntypedFormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  asyncScheduler,
  Subscription,
  Observable,
  of,
  Subject,
  timer,
} from 'rxjs';
import {
  catchError,
  debounce,
  debounceTime,
  defaultIfEmpty,
  first,
  map,
  observeOn,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  filter,
} from 'rxjs/operators';
import {
  AmenityFilterOption,
  WorkstationSearchDataSource,
} from 'src/app/DataSources/workstation-search.data-source';
import { AlertInfo, AlertService, AlertType } from 'src/app/Services/Alert/alert.service';
import { BuildingService } from 'src/app/Services/Building/building.service';
import { ReservationService } from 'src/app/Services/Reservation/reservation.service';
import { UserService } from 'src/app/Services/User/user.service';
import { WorkstationService } from 'src/app/Services/Workstation/workstation.service';
import { FavouriteService } from 'src/app/Services/Favourite/favourite.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { SearchBarOption, SearchFilters } from '../search-bar/search-bar.component';
import { TableColumn, TableIcon } from '../table/table.component';
import { RoleConst } from 'src/app/Models/roles.const';
import { CurrentUserService } from 'src/app/Services/CurrentUser/current-user.service';
import { ThemeService } from 'src/app/theme.service';
import {
  getWorkstationAmenityLabel,
  getWorkstationAmenityTableIconName,
} from 'src/app/utils/workstation-amenity-display.util';

/**
 * Home page container for reservation workflow.
 *
 * Responsibilities:
 * - Provide building/floor/time/date filters for workstation search.
 * - Manage delegate booking behavior.
 * - Render availability results and initiate reservation requests.
 */
@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [DatePipe],
})
export class HomeComponent implements OnInit, OnDestroy {
  // ---------------------------------------------------------------------
  // Template-bound view state
  // ---------------------------------------------------------------------
  displayedColumns: string[] = ['favourite', 'deskNumber', 'floorNumber', 'features', 'actions'];
  dataSource?: WorkstationSearchDataSource;
  tablePageIndex = 0;
  tablePageSize = 10;
  minDate: Date;
  maxDate: Date;
  hasDelegations = false;
  readonly hasDelegations$ = new BehaviorSubject<boolean>(false);
  isSuperUser = false;
  isUserAdmin = false;
  isEditor = false;
  disableReserve: boolean = false;
  timeConflictMessage: string | null = null;
  searchSubmitted: boolean = false;
  hasSearched: boolean = false;
  clearAlertOnNewPage: boolean = true;

  bookingForOptions: SearchBarOption[] = [];
  readonly bookingForOptions$ = new BehaviorSubject<SearchBarOption[]>([]);

  locationOptions: SearchBarOption[] = [];
  readonly locationOptions$ = new BehaviorSubject<SearchBarOption[]>([]);

  floorOptions: SearchBarOption[] = [];
  readonly floorOptions$ = new BehaviorSubject<SearchBarOption[]>([]);

  timePeriodOptions: SearchBarOption[] = [];
  readonly timePeriodOptions$ = new BehaviorSubject<SearchBarOption[]>([]);

  private endDateEditedBeforeStartDate = false;

  favouriteWorkstationIds: number[] = [];
  favouritesInResultsCount: number = 0;
  private pendingFavouriteWorkstationIds: number[] = [];

  /** Amenities available to filter the current results by */
  amenityFilters: AmenityFilterOption[] = [];
  selectedAmenityIds: number[] = [];

  /** Desk name search applied to the full result set before pagination */
  deskSearchControl: UntypedFormControl = new UntypedFormControl('');

  get hasDeskSearchText(): boolean {
    return !!(this.deskSearchControl.value ?? '').trim();
  }

  /** Show the count/search row whenever the location/floor/date search returned desks */
  get showResultsToolbar(): boolean {
    return this.hasSearched && !!this.dataSource?.hasUnfilteredResults;
  }
  /** Ensures last-booking-selection pre-fill runs only once per page load, not on every user$ emission. */
  private lastBookingSelectionApplied = false;

  /** Tracks which fields were autofilled from last booking selection */
  autofilledFields = { building: false, floor: false, timeSlot: false };
  isAutofillInProgress: boolean = false;
  private timePeriodsLoaded: boolean = false;
  private prefillFlowFinished: boolean = false;

  get hasAutofilledFields(): boolean {
    return (
      this.autofilledFields.building ||
      this.autofilledFields.floor ||
      this.autofilledFields.timeSlot
    );
  }

  get allFavouritesUnavailable(): boolean {
    // Suppress the notice while booking for a delegate. The booker is shown the delegate's
    // favourites read-only and shouldn't be nudged about the delegate's saved-elsewhere desks.
    if (!this.canEditFavourites) {
      return false;
    }
    return (
      this.hasSearched &&
      this.favouriteWorkstationIds.length > 0 &&
      this.favouritesInResultsCount === 0
    );
  }

  /**
   * Shows the no workstations panel when either the backend search has no rows
   * or the toggled search filter chips creates a table with no rows.
   */
  get showNoWorkstationsState(): boolean {
    return this.noWorkstations || (this.hasSearched && (this.dataSource?.dataCount ?? 0) === 0);
  }

  /**
   * True only when the booking target is the logged-in user. While booking for a delegate the
   * heart icons are read-only — the booker may see the delegate's saved favourites pinned at
   * the top, but cannot add or remove favourites on their behalf.
   */
  get canEditFavourites(): boolean {
    const targetUserId = this.getFavouritesTargetUserId();
    return targetUserId != null && targetUserId === this.me?.id;
  }

  // buildings: any[];
  buildingsByLoggedUser: any[] = [];
  buildingsByLoggedUser$: Observable<any[]> = of([]);
  buildingsBySelectedUser: any[] = [];
  buildingsBySelectedUser$: Observable<any[]> = of([]);
  timePeriods: any[] = [];
  timePeriods$: Observable<any[]> = of([]);
  floors: any[] = [];
  userId: number;
  buildingId: number;

  // Form model for reservation filters.
  homeForm?: UntypedFormGroup;

  // selectedBuilding: any;
  // selectedBuildingProcedures: any[] = [];
  // selectBuildingFloorPlans: any[] = [];
  buildingFloorPlans: any[] = [];

  // ---------------------------------------------------------------------
  // Backing fields with side-effect setters
  // ---------------------------------------------------------------------
  private _selectedDelegate: any;
  get selectedDelegate() {
    return this._selectedDelegate;
  }
  set selectedDelegate(val: any) {
    this.dataSource?.clearTable();
    this.clearTimeConflictState();
    if (val.id > 0) {
      this._selectedDelegate = val;
    } else {
      this._selectedDelegate = null;
    }
    // Favourites belong to whoever the booking is being made for, so any change to the
    // selected delegate must refresh the list. Clear the local cache first so the previous
    // user's favourites cannot momentarily appear pinned against the new user's results,
    // and abandon any in-flight toggle requests for the previous target.
    this.favouriteWorkstationIds = [];
    this.pendingFavouriteWorkstationIds = [];
    this.dataSource?.reSortFavourites(this.favouriteWorkstationIds);
    this.loadFavourites();
  }
  private _selectedBuildingId: number;
  private me: any;
  get selectedBuildingId() {
    return this._selectedBuildingId;
  }
  set selectedBuildingId(val: number) {
    this.dataSource?.clearTable();
    this._selectedBuildingId = val;
  }
  private _startDate?: Date;
  get startDate(): Date | undefined {
    return this._startDate;
  }
  set startDate(val: Date | undefined) {
    this.dataSource?.clearTable();
    this._startDate = val;
    this.clearTimeConflictState();
  }
  private _isRepeat: boolean = false;
  get isRepeat() {
    return this._isRepeat;
  }
  set isRepeat(val: boolean) {
    this.dataSource?.clearTable();
    this._isRepeat = val;
    this.clearTimeConflictState();
  }
  private _isRepeatDisable: boolean = false;
  get isRepeatDisable() {
    return this._isRepeatDisable;
  }
  set isRepeatDisable(val: boolean) {
    this._isRepeatDisable = val;

    if (val === true) {
      this.endDate = undefined;
      this.clearEndDays();
    }
  }
  private _dayMask: number;
  get dayMask() {
    return this._dayMask;
  }
  set dayMask(val: number) {
    this.dataSource?.clearTable();
    this._dayMask = val;
    this.clearTimeConflictState();
  }
  private _endDate?: Date;
  get endDate(): Date | undefined {
    return this._endDate;
  }
  set endDate(val: Date | undefined) {
    this.dataSource?.clearTable();
    this._endDate = val;
    this.clearTimeConflictState();
  }
  /** Bound to the existing time-period dropdown selection. */
  selectedTimePeriodId: number | null = null;
  private _selectedFloorIds: number[] = [];
  get selectedFloorIds() {
    return this._selectedFloorIds;
  }
  set selectedFloorIds(val: number[]) {
    this.dataSource?.clearTable();
    this._selectedFloorIds = val;
    this.clearTimeConflictState();
  }

  userSearchControl: UntypedFormControl = new UntypedFormControl();
  userSearchSub?: Subscription;
  searchedUsers: any[] = [];
  noResultsLabel: string = 'No Results';

  // Building card document selectors.
  buildingFpFormControl: UntypedFormControl = new UntypedFormControl();
  buildingBpFormControl: UntypedFormControl = new UntypedFormControl();

  noWorkstations = false;
  readonly effectiveTheme = this.themeService.effectiveTheme;

  readonly resultsTableColumns: TableColumn<any>[] = [
    {
      id: 'favourite',
      header: 'Favourite',
      showHeader: false,
      type: 'favourite',
      align: 'center',
      columnWidth: 75,
      showFavourite: (row) => row?.id != 0,
      isFavourite: (row) => this.isFavourite(row?.id),
      favouriteDisabled: (row) =>
        this.isFavouriteRequestPending(row?.id) || !this.canEditFavourites,
      favouriteAriaLabel: (row) => this.favouriteIconLabel(row?.id),
    },
    {
      id: 'deskNumber',
      header: 'Desk No.',
      showHeader: true,
      type: 'text',
      columnWidth: 235,
      text: (row) => row?.number ?? '',
    },
    {
      id: 'floorNumber',
      header: 'Floor No.',
      showHeader: true,
      type: 'text',
      columnWidth: 200,
      text: (row) => row?.floor?.name ?? '',
    },
    {
      id: 'features',
      header: 'Features',
      showHeader: true,
      type: 'icons',
      icons: (row) => this.getRowAmenityIcons(row),
    },
    {
      id: 'actions',
      header: 'Actions',
      showHeader: false,
      type: 'actionGroup',
      align: 'right',
      columnWidth: 300,
      actions: (row) => ({
        link: row?.floor?.floorPlans?.[0]
          ? {
              label: 'Show floor plan',
              href: row.floor.floorPlans[0].floorPlanUrl,
              target: '_blank',
            }
          : null,
        button: {
          label: 'Book',
          ariaLabel: `Book workstation ${row?.number ?? ''}`,
          disabled: this.disableReserve,
          cssClass: 'reserve-button',
          variant: 'raised',
        },
      }),
    },
  ];

  @ViewChild('resultsHeading') resultsHeading?: ElementRef<HTMLElement>;
  @ViewChild('deskSearchInput') deskSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('resultsSection') resultsSection?: ElementRef<HTMLElement>;
  private readonly destroy$ = new Subject<void>();

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------
  constructor(
    private workstationService: WorkstationService,
    private buildingService: BuildingService,
    private reservationService: ReservationService,
    private alertService: AlertService,
    private userService: UserService,
    private currentUserService: CurrentUserService,
    private favouriteService: FavouriteService,
    private themeService: ThemeService,
    private router: Router,
    public dialog: MatDialog,
    private datePipe: DatePipe,
    private fb: UntypedFormBuilder
  ) {
    // this.minDate = new Date(new Date().getTime() + (1000 * 60 * 60 * 24));// prevent same day booking
    this.minDate = new Date(new Date().getTime()); // enable same day booking
    let tmpDate = this.minDate;
    let day = tmpDate.getDay();
    if (day == 0) {
      this.minDate.setDate(tmpDate.getDate() + 1);
    }
    if (day == 6) {
      this.minDate.setDate(tmpDate.getDate() + 2);
    }
    this.maxDate = new Date(
      new Date().getTime() + 1000 * 60 * 60 * 24 * this.getOpeningDays(false)
    );
  }

  ngOnInit(): void {
    this.timePeriodsLoaded = false;
    this.prefillFlowFinished = false;
    this.updateAutofillInProgress();

    this.dataSource = new WorkstationSearchDataSource(this.workstationService);
    this.dataSource.favouritesInResultsCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
      this.favouritesInResultsCount = count;
    });
    this.dataSource.availableAmenities$.pipe(takeUntil(this.destroy$)).subscribe((amenities) => {
      this.amenityFilters = amenities;
      this.selectedAmenityIds = []; // Reset amenity ids
      this.deskSearchControl.setValue('', { emitEvent: false }); // Reset desk search
    });
    this.deskSearchControl.valueChanges
      .pipe(
        debounce((value: string) => ((value ?? '').trim() ? timer(300) : of(null))),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => this.applyDeskSearch(value));
    this.dayMask = 0;
    this.applyUser(this.currentUserService.currentUser);
    this.currentUserService.user$
      .pipe(
        filter((user) => !!user),
        takeUntil(this.destroy$)
      )
      .subscribe((user) => this.applyUser(user));

    this.timePeriods$ = this.reservationService.getTimeSlots().pipe(
      map((data) => data?.value ?? []),
      tap((periods) => {
        this.timePeriods = periods;
        this.refreshTimePeriodOptions();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // Legacy filters are hidden, so explicitly subscribe to hydrate search-bar options.
    this.timePeriods$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.timePeriodsLoaded = true;
        this.updateAutofillInProgress();
      },
      error: () => {
        this.timePeriodsLoaded = true;
        this.updateAutofillInProgress();
      },
    });

    // The `Booking for` dropdown's visibility and contents are both derived from a single
    // call to getDelegations('') in applyUser — see searchForDelegation below for how
    // hasDelegations is set. No separate hasDelegations() API call is needed.

    // Pre-fill Location, Floor, and Time Period from the user's last confirmed booking.
    // Waits for the user identity to resolve, then fetches the saved selection and
    // sequences building -> floor load -> field application so all dropdowns populate correctly.
    this.currentUserService.user$
      .pipe(
        filter((user) => !!user),
        take(1),
        switchMap(() => this.userService.getLastBookingSelection()),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (resp) => {
          if (this.lastBookingSelectionApplied) {
            this.prefillFlowFinished = true;
            this.updateAutofillInProgress();
            return;
          }
          this.lastBookingSelectionApplied = true;

          const selection = resp?.value;
          if (!selection?.hasSelection) {
            this.prefillFlowFinished = true;
            this.updateAutofillInProgress();
            return;
          }

          const { buildingId, floorId, timeSlotId } = selection;

          // Wait for the selected-user buildings observable to emit, then load floors
          // for the pre-filled building and apply floor + time period once floors are ready.
          this.buildingsBySelectedUser$.pipe(take(1)).subscribe((buildings: any[]) => {
            const buildingExists: boolean = (buildings ?? []).some((b) => b?.id === buildingId);
            if (buildingExists) {
              this.autofilledFields.building = true;
              this.buildingId = buildingId;
              this.selectedBuildingId = buildingId;
            }

            // Trigger floor load for the pre-filled building.
            if (this.userId > 0 && this.buildingId > 0) {
              this.buildingService
                .getFloorsbyUser(this.buildingId, this.userId)
                .pipe(take(1))
                .subscribe({
                  next: (data) => {
                    this.floors = data.value ?? [];
                    this.refreshFloorOptions();

                    // Apply floor only if it is present in the loaded list.
                    const floorExists = this.floors.some((f) => f.id === floorId);
                    if (floorExists) {
                      this.autofilledFields.floor = true;
                      this.selectedFloorIds = [floorId];
                      this.homeForm?.controls['selectFloors']?.patchValue([floorId]);
                      this.checkOTCTag();
                    }

                    // Time period is always applied - not subject to availability.
                    if (timeSlotId) {
                      this.autofilledFields.timeSlot = true;
                    }
                    this.selectedTimePeriodId = timeSlotId;
                    this.prefillFlowFinished = true;
                    this.updateAutofillInProgress();
                  },
                  error: () => {
                    this.prefillFlowFinished = true;
                    this.updateAutofillInProgress();
                  },
                });
            } else {
              this.prefillFlowFinished = true;
              this.updateAutofillInProgress();
            }
          });
        },
        () => {
          this.prefillFlowFinished = true;
          this.updateAutofillInProgress();
        }
      );

    this.userSearchSub = this.userSearchControl.valueChanges
      .pipe(debounceTime(750), takeUntil(this.destroy$))
      .subscribe((value) => this.searchForDelegation(value));

    this.homeForm = this.fb.group({
      selectFloors: new UntypedFormControl(''),
      startDate: new UntypedFormControl(''),
    });

    this.clearAlertOnNewPage = true;
  }

  private updateAutofillInProgress(): void {
    this.isAutofillInProgress = !(this.timePeriodsLoaded && this.prefillFlowFinished);
  }

  /* setLocation(building: any) {
      this.selectedBuilding = building;
      this.selectedBuildingId = this.selectedBuilding.id;
  
      this.buildingService.getFloors(this.selectedBuildingId).subscribe(floors => {
        this.floors = floors.value;
      });
  
      this.selectedBuildingProcedures = [];
      for (let bp of building?.buildingProcedures) {
        this.selectedBuildingProcedures.push({ name: bp.fileName, id: bp.id })
      }
  
      this.selectBuildingFloorPlans = [];
      for (let f of building?.floors) {
        for (let fp of f?.floorPlans) {
          this.selectBuildingFloorPlans.push({ name: fp.fileName, url: fp.floorPlanUrl })
        }
      }
    } */

  private isWeekend(date: Date | undefined): boolean {
    if (!this.isValidDate(date) || date === undefined) {
      return false;
    }

    const day: number = date.getDay();
    return day === 0 || day === 6;
  }

  /** Returns true only when conflict-check fields are fully valid. */
  private canCheckTimeConflict(): boolean {
    if (this.selectedTimePeriodId == null || this.selectedTimePeriodId <= 0) {
      return false;
    }

    if (!this.isValidDate(this.startDate)) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startAtMidnight = new Date(this.startDate);
    startAtMidnight.setHours(0, 0, 0, 0);

    if (startAtMidnight < today) {
      return false;
    }

    if (this.startDate > this.maxDate) {
      return false;
    }

    if (this.isRepeat) {
      if (!this.isValidDate(this.endDate)) {
        return false;
      }

      if (this.endDate < this.startDate) {
        return false;
      }

      if (this.endDate > this.maxDate) {
        return false;
      }

      if (this.dayMask == null || this.dayMask <= 0) {
        return false;
      }

      if (!this.hasOverlappingDays()) {
        return false;
      }
    }

    return true;
  }

  /** Clears booking lock from a previous time-conflict result; call when search inputs change. */
  private clearTimeConflictState(): void {
    this.disableReserve = false;
    this.timeConflictMessage = null;
  }

  /** Maps time-conflict API payload to `disableReserve`, message, and optional warning banner. */
  private applyTimeConflictResponse(data: any): void {
    const v = data?.value;
    let hasTimeConflict = false;
    let timeConflictMessage = '';
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      hasTimeConflict = !!(v as Record<string, unknown>)['hasTimeConflict'];
      timeConflictMessage = String((v as Record<string, unknown>)['message'] ?? '');
    } else if (typeof v === 'number' && v === 1) {
      // Legacy delegate response: bare 1 implied no structured conflict payload.
      hasTimeConflict = false;
      timeConflictMessage = '';
    }

    if (hasTimeConflict) {
      this.disableReserve = true;
      this.timeConflictMessage = timeConflictMessage;
      this.alertService.setAlert(<AlertInfo>{
        title: 'Conflict with existing booking',
        message: timeConflictMessage,
        type: AlertType.Warning,
        closeable: false, // make uncloseable, there's logic in the ngOnDestroy function to clear the alert on new page load
      });
    } else {
      this.disableReserve = false;
      this.timeConflictMessage = null;
    }
  }

  /**
   * Runs after validation: loads availability; time conflict is checked here only (not on each field change).
   */
  private finalizeSearchLoad(shouldScrollToResults: boolean, resetPaginator: boolean = true): void {
    if (!this.disableReserve) {
      this.alertService.clearAlert();
    }

    this.searchSubmitted = false;
    this.hasSearched = false;
    if (resetPaginator) {
      this.tablePageIndex = 0;
    }
    this.dataSource
      ?.loadWorkstations(
        this.userId,
        this.startDate,
        this.endDate,
        this.dayMask,
        this.selectedTimePeriodId,
        this.selectedFloorIds,
        this.favouriteWorkstationIds,
        this.tablePageIndex,
        this.tablePageSize,
        this.isRepeat,
        this.disableReserve
      )
      .then((data: any) => {
        this.noWorkstations = data === 0;
        this.hasSearched = true;
        if (shouldScrollToResults && !this.disableReserve) {
          setTimeout(() => this.scrollToResults(), 100);
        }
      });
  }

  /**
   * Builds per-building floor-plan lookup used by the building card
   * card selectors.
   */
  setBuildingsFloorPlans() {
    for (let b of this.buildingsByLoggedUser) {
      this.buildingFloorPlans[b.id] = [];
      for (let f of b?.floors) {
        for (let fp of f?.floorPlans) {
          if (fp !== null) {
            this.buildingFloorPlans[b.id].push({ name: fp.fileName, url: fp.floorPlanUrl });
          }
        }
      }
    }
  }

  /**
   * Loads buildings for the selected booking user (self or delegate).
   */
  getBuildingBySelectedUser() {
    this.buildingsBySelectedUser$ = this.buildingService
      .getBuildingsBySelectedUser(this.userId)
      .pipe(
        observeOn(asyncScheduler),
        map((data) => data?.value?.result ?? []),
        tap((buildings) => {
          this.buildingsBySelectedUser = buildings;
          this.refreshLocationOptions();
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Trigger the stream so search-bar location options are populated even when legacy UI is hidden.
    this.buildingsBySelectedUser$.pipe(take(1)).subscribe();
  }

  /** Datepicker guard: block weekend selection. */
  weekdayFilter = (d: Date | null): boolean => {
    if (!d) {
      return false;
    }

    const candidate = new Date(d);
    candidate.setHours(0, 0, 0, 0);

    const min = new Date(this.minDate);
    min.setHours(0, 0, 0, 0);

    const max = new Date(this.maxDate);
    max.setHours(0, 0, 0, 0);

    const day = candidate.getDay();
    const isWeekday = day !== 0 && day !== 6;
    const inRange = candidate >= min && candidate <= max;

    return isWeekday && inRange;
  };

  legacyRepeatChange(event: MatCheckboxChange) {
    this.isRepeat = event.checked;
    if (!this.isRepeat) {
      this.endDateEditedBeforeStartDate = false;
    }
  }

  /** Tracks whether end date was manually set before start date selection. */
  legacyOnEndDateChanged(): void {
    if (!this.isValidDate(this.endDate)) {
      this.endDateEditedBeforeStartDate = false;
      return;
    }

    this.endDateEditedBeforeStartDate = !this.isValidDate(this.startDate);
  }

  clearEndDays() {
    this.legacyDayClicked(1, null, false);
    this.legacyDayClicked(2, null, false);
    this.legacyDayClicked(3, null, false);
    this.legacyDayClicked(4, null, false);
    this.legacyDayClicked(5, null, false);
  }

  legacyDayClicked(shift: number, event: MatCheckboxChange | null, setValue?: boolean) {
    const val = 1 << shift;
    const shouldSet = event?.checked ?? setValue ?? false;
    const currentMask = this.dayMask ?? 0;

    if (shouldSet) {
      this.dayMask = currentMask | val;
      return;
    }

    this.dayMask = currentMask & ~val;
  }

  legacyIsDaySelected(shift: number): boolean {
    const currentMask = this.dayMask ?? 0;
    return (currentMask & (1 << shift)) > 0;
  }

  searchForDelegation(searchStr: string) {
    this.noResultsLabel = 'Searching...';
    this.userService
      .getDelegations(searchStr)
      .pipe(observeOn(asyncScheduler), takeUntil(this.destroy$))
      .subscribe((data) => {
        this.noResultsLabel = 'No Results';
        const delegators = data?.value ?? [];
        this.searchedUsers = [...delegators];
        if (this.me) {
          this.searchedUsers.push(this.me);
        }
        // The dropdown should be shown when the user has delegators — capture that here so
        // hasDelegations is derived from the same call that populates the list. Only flip it
        // to true (never to false): a SuperUser/UserAdmin still gets the dropdown even when
        // their typed filter narrows to zero matches.
        this.setHasDelegations(this.hasDelegations || delegators.length > 0);
        this.refreshBookingForOptions();
      });
  }

  updatedFloors() {
    this.selectedFloorIds = [];
    this.floors = [];
    this.refreshFloorOptions();
    // Re-evaluate OTC constraints immediately when floor selection is cleared.
    this.checkOTCTag();
    if (this.userId > 0 && this.buildingId > 0) {
      this.buildingService.getFloorsbyUser(this.buildingId, this.userId).subscribe((floors) => {
        this.floors = floors.value;
        this.refreshFloorOptions();
        // Re-evaluate in case selection/options changed by location/user switch.
        this.checkOTCTag();
      });
    }
  }

  onBookingForChange(userId: number | null): void {
    if (userId == null || userId <= 0 || this.userId === userId) {
      return;
    }

    const selectedUser: any = this.findUserById(userId) ?? this.me;
    if (!selectedUser) {
      return;
    }

    this.selectedDelegate = selectedUser;
    this.userId = userId;

    // Keep current search-bar filters persistent when Booking For changes.
    // Only refresh user-scoped location options; do not reset or re-autofill.
    this.getBuildingBySelectedUser();
    this.updatedFloors();
  }

  onLocationChange(buildingId: number | null): void {
    if (buildingId == null || buildingId <= 0) {
      this.buildingId = 0;
      this.selectedBuildingId = 0;
      this.updatedFloors();
      return;
    }

    if (this.buildingId === buildingId) {
      return;
    }

    this.buildingId = buildingId;
    this.selectedBuildingId = buildingId;
    this.updatedFloors();
  }

  onFloorSelectionChange(floorIds: number[]): void {
    this.selectedFloorIds = floorIds ?? [];
    this.checkOTCTag();
  }

  onFiltersChanged(startDate: Date | null): void {
    const nextStartDate = startDate ?? undefined;
    const hasStartDateChanged =
      this.toDateTimeValue(this.startDate) !== this.toDateTimeValue(nextStartDate);

    this.startDate = nextStartDate;

    if (hasStartDateChanged) {
      this.checkOTCTag();
    }

    this.dataSource?.clearTable();
    this.noWorkstations = false;
    this.hasSearched = false;
    this.clearTimeConflictState();
  }

  onRepeatEnabledChanged(enabled: boolean): void {
    this.isRepeat = enabled;
  }

  onSelectedDaysChanged(selectedDays: Record<'M' | 'T' | 'W' | 'R' | 'F', boolean>): void {
    const keys = Object.keys(selectedDays) as Array<'M' | 'T' | 'W' | 'R' | 'F'>;
    const activeDays = keys.filter((day) => selectedDays[day]);
    this.dayMask = this.toDayMask(activeDays);
  }

  onPeriodChanged(periodId: number | null): void {
    this.selectedTimePeriodId = periodId;
  }

  onEndDateChanged(endDate: Date | null): void {
    this.endDate = endDate ?? undefined;
  }

  private toDateTimeValue(value: Date | undefined): number | null {
    return this.isValidDate(value as Date) ? (value as Date).getTime() : null;
  }

  get selectedBookingForId(): number | null {
    return this.selectedDelegate?.id ?? null;
  }

  get bookingForSelfOptionLabel(): string {
    const name = `${this.me?.firstName ?? ''} ${this.me?.lastName ?? ''}`.trim();
    return name ? `${name} (Me)` : 'Me';
  }

  get selectedBookingForLabel(): string {
    const selectedUser = this.selectedDelegate;
    if (!selectedUser) {
      return 'Myself';
    }

    if (selectedUser.id === this.me?.id) {
      return 'Myself';
    }

    return `${selectedUser.firstName ?? ''} ${selectedUser.lastName ?? ''}`.trim() || 'Myself';
  }

  get selectedLocationId(): number | null {
    return this.buildingId > 0 ? this.buildingId : null;
  }

  get selectedFloorIdsForSearchBar(): number[] {
    return this.selectedFloorIds ?? [];
  }

  get selectedPeriodIdForSearchBar(): number | null {
    return this.selectedTimePeriodId;
  }

  get selectedStartDateForSearchBar(): Date | null {
    return this.isValidDate(this.startDate) ? this.startDate : null;
  }

  get selectedEndDateForSearchBar(): Date | null {
    return this.isValidDate(this.endDate) ? this.endDate : null;
  }

  onSearchRequested(searchFilters: SearchFilters): void {
    if (
      searchFilters.bookingForId != null &&
      searchFilters.bookingForId > 0 &&
      this.userId !== searchFilters.bookingForId
    ) {
      const selectedUser = this.findUserById(searchFilters.bookingForId);
      if (selectedUser) {
        this.selectedDelegate = selectedUser;
        this.userId = searchFilters.bookingForId;
      }
    }

    if (searchFilters.locationId != null && searchFilters.locationId > 0) {
      this.buildingId = searchFilters.locationId;
      this.selectedBuildingId = searchFilters.locationId;
    }

    this.startDate = searchFilters.startDate ?? undefined;
    this.selectedFloorIds = searchFilters.floorIds ?? [];
    this.checkOTCTag();

    this.selectedTimePeriodId = searchFilters.periodId;
    const repeatAllowed = !this.isRepeatDisable;
    this.isRepeat = repeatAllowed ? searchFilters.repeatEnabled : false;
    this.endDate = this.isRepeat ? (searchFilters.endDate ?? undefined) : undefined;
    this.dayMask = this.isRepeat ? this.toDayMask(searchFilters.selectedDays) : 0;

    this.search(true);
  }

  /** Time period list: use selectionChange only (not two-way bind to a setter) so mat-select can open reliably. */
  legacyOnTimePeriodSelectionChange(event: MatSelectChange): void {
    this.dataSource?.clearTable();
    this.clearTimeConflictState();
    this.selectedTimePeriodId = event.value;
  }

  legacyBuildingSelected(event: MatSelectChange) {
    if (event.value == 0) {
      event.source.writeValue(null);
    } else {
      this.buildingId = event.value;
      this.updatedFloors();
    }
    this.isRepeatDisable = false;
    this.clearTimeConflictState();
  }
  legacyUserSelected(event: MatSelectChange) {
    if (event.value.id == 0) {
      event.source.writeValue(null);
    } else {
      this.userId = event.value.id;
      this.getBuildingBySelectedUser();
    }
    this.clearTimeConflictState();
  }

  private refreshBookingForOptions(): void {
    const sourceUsers = this.hasDelegations ? this.searchedUsers : this.me ? [this.me] : [];

    const seen = new Set<number>();
    this.bookingForOptions = sourceUsers
      .filter((u) => !!u && typeof u.id === 'number' && u.id > 0)
      .filter((u) => {
        if (seen.has(u.id)) {
          return false;
        }
        seen.add(u.id);
        return true;
      })
      .map((u) => ({
        id: u.id,
        label:
          u.id === this.me?.id
            ? 'Myself'
            : `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `User ${u.id}`,
      }));

    this.bookingForOptions$.next(this.bookingForOptions);
  }

  private refreshLocationOptions(): void {
    this.locationOptions = (this.buildingsBySelectedUser ?? []).map((b) => ({
      id: b.id,
      label: b.name,
    }));

    this.locationOptions$.next(this.locationOptions);
  }

  private refreshFloorOptions(): void {
    this.floorOptions = (this.floors ?? []).map((f) => ({
      id: f.id,
      label: f.name,
    }));

    this.floorOptions$.next(this.floorOptions);
  }

  private refreshTimePeriodOptions(): void {
    this.timePeriodOptions = (this.timePeriods ?? []).map((p) => ({
      id: p.id,
      label: p.name,
    }));

    this.timePeriodOptions$.next(this.timePeriodOptions);
  }

  private findUserById(userId: number): any | null {
    if (this.me?.id === userId) {
      return this.me;
    }

    return this.searchedUsers.find((u) => u?.id === userId) ?? null;
  }

  private toDayMask(days: Array<'M' | 'T' | 'W' | 'R' | 'F'>): number {
    const mapByDay: Record<'M' | 'T' | 'W' | 'R' | 'F', number> = {
      M: 1,
      T: 2,
      W: 3,
      R: 4,
      F: 5,
    };

    return (days ?? []).reduce((mask, day) => mask | (1 << mapByDay[day]), 0);
  }

  /**
   * Toggles the building card section visibility and updates button label.
   */
  show(): void {
    var a = document.getElementById('bldgs');
    var b = document.getElementById('show');
    if (!a || !b) {
      return;
    }
    var currentDisplay = a.style.display || window.getComputedStyle(a).display;
    if (currentDisplay == 'none') {
      a.style.display = 'flex';
      b.textContent = 'Hide buildings';
    } else if (currentDisplay == 'flex') {
      a.style.display = 'none';
      b.textContent = 'Display buildings';
    }
  }

  // /** Navigates from Home to Divisions list view. */
  // showdiv(): void {
  //   window.location.href = '/divisions';
  // }

  /** Executes workstation search with the currently selected filters. */
  search(
    shouldScrollToResults: boolean = false,
    skipTimeConflictCheck: boolean = false,
    resetPaginator: boolean = true
  ): void {
    this.autofilledFields = { building: false, floor: false, timeSlot: false };
    this.searchSubmitted = true;

    if (!skipTimeConflictCheck) {
      this.alertService.clearAlert();
    }

    // Silent gate for non-user-actionable race conditions. `userId` is
    // populated asynchronously from CurrentUserService and `buildingId`
    // is derived from the building dropdown state; they are not a
    // submittable field on this form, so bannering them would be
    // confusing and their missing state is never reachable by clicking
    // Search in a normal flow. Any downstream validation (floors, time
    // period, etc.) will raise an actionable banner if applicable.
    let anyFieldIsEmpty: boolean =
      this.userId == null || this.userId <= 0 || this.buildingId == null || this.buildingId <= 0;

    if (anyFieldIsEmpty) {
      return;
    }

    const bookingWindowExceededError: string = this.validateEndDateWindowWhenSetBeforeStartDate();
    if (bookingWindowExceededError != null) {
      this.alertService.setAlert(<AlertInfo>{
        title: 'Booking Window Exceeded',
        message: bookingWindowExceededError,
        type: AlertType.Error,
        closeable: true,
      });
      return;
    }

    // Mirror WorkstationService.SearchAvailable so every user-facing
    // ValidationException that PROD surfaced via an HTTP 400 is now
    // raised as the same "Error Getting Available Workstations" banner
    // (title/shape match base.service.handleError), but without the
    // failing network call. Strings are character-identical to the
    // backend so Application Insights can still attribute causes.
    // Note: we intentionally do NOT route through this.handleError()
    // because that helper re-invokes this.search() after setting the
    // alert, which would recurse indefinitely on a validation failure.
    const validationError: { showBanner: boolean; message: string } | null =
      this.validateSearchFilters();
    if (validationError != null) {
      if (validationError.showBanner) {
        const bookingWindowMessage = this.getBookingWindowExceededMessage();
        if (validationError.message === bookingWindowMessage) {
          this.alertService.setAlert(<AlertInfo>{
            title: 'Booking Window Exceeded',
            message: validationError.message,
            type: AlertType.Error,
            closeable: true,
          });
          return;
        }

        this.alertService.setAlert(<AlertInfo>{
          title: 'Error Getting Available Workstations',
          message: validationError.message,
          type: AlertType.Error,
          closeable: true,
        });
      }

      return;
    }

    if (skipTimeConflictCheck) {
      this.finalizeSearchLoad(false, resetPaginator);
      return;
    } else if (!this.canCheckTimeConflict()) {
      this.clearTimeConflictState();
      this.finalizeSearchLoad(shouldScrollToResults, resetPaginator);
      return;
    }

    this.reservationService
      .checkForTimeConflict(
        this.selectedTimePeriodId,
        this.getReservationBodyObject(),
        this.getDelegationId()
      )
      .pipe(
        defaultIfEmpty(null as any),
        take(1),
        catchError(() => of(null))
      )
      .subscribe((data) => {
        if (data == null) {
          this.clearTimeConflictState();
        } else {
          this.applyTimeConflictResponse(data);
        }
        this.finalizeSearchLoad(shouldScrollToResults, resetPaginator);
      });
  }

  /** Scrolls to search results if they exist and are not fully visible. */
  private scrollToResults(): void {
    if (!this.resultsSection?.nativeElement) {
      return;
    }

    const rect = this.resultsSection.nativeElement.getBoundingClientRect();
    const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isFullyVisible) {
      this.resultsSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        this.resultsHeading?.nativeElement?.focus();
      }, 300);
    }
  }

  dayMap: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /**
   * Builds reservation confirmation text and prompts user confirmation.
   */
  reserveClicked(ws: any) {
    if (this.disableReserve) {
      this.alertService.setAlert(<AlertInfo>{
        title: 'Conflict with existing booking',
        message:
          this.timeConflictMessage ??
          'You have a conflicting reservation for the selected date(s).',
        type: AlertType.Warning,
        closeable: true,
      });
      return;
    }

    // let periodName: string = this.timePeriods.find((a) => a.id === this.selectedTimePeriodId).name;
    // let userMessage: string = '';
    // if (this.selectedDelegate) {
    //   if (this.selectedDelegate.id != this.me.id) {
    //     userMessage = ` for user '${this.selectedDelegate.firstName} ${this.selectedDelegate.lastName}'`;
    //   }
    // }

    // let duration: string = '';

    // let selectedDays: string[] = [];
    // if (this.isRepeat) {
    //   for (var i = 0; i < 7; i++) {
    //     if (!(((1 << i) & this.dayMask) > 0)) continue;
    //     selectedDays.push(this.dayMap[i]);
    //   }

    //   var days = selectedDays.join(', ');

    //   duration = `from ${this.datePipe.transform(this.startDate, 'EEEE yyyy/MM/dd')} to ${this.datePipe.transform(this.endDate, 'EEEE yyyy/MM/dd')}
    //   each ${days} for the '${periodName}' period`;
    // } else {
    //   duration = `on ${this.datePipe.transform(this.startDate, 'EEEE yyyy/MM/dd')} for the '${periodName}' period`;
    // }
    // const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
    //   data: `Are you sure you would like to book workstation ${ws.number} on ${ws.floor.name}${userMessage}, ${duration}?`,
    // });

    // dialogRef
    //   .afterClosed()
    //   .pipe(first())
    //   .subscribe((result) => {
    //     if (result) {
    //       this.reserveWorkstation(ws);
    //     }
    //   });

    this.reserveWorkstation(ws);
  }

  /** Submits reservation request for the selected workstation. */
  reserveWorkstation(ws: any) {
    // Frontend validation for POST Reservation/AddBooking
    // There were some users who got past the validation in the search() function (400 error code)
    const validationError: { showBanner: boolean; message: string } | null =
      this.validateSearchFilters();
    if (validationError != null) {
      if (validationError.showBanner) {
        const bookingWindowMessage = this.getBookingWindowExceededMessage();
        if (validationError.message === bookingWindowMessage) {
          this.alertService.setAlert(<AlertInfo>{
            title: 'Booking Window Exceeded',
            message: validationError.message,
            type: AlertType.Error,
            closeable: true,
          });
          return;
        }

        this.alertService.setAlert(<AlertInfo>{
          title: 'Error Booking Workstation',
          message: validationError.message,
          type: AlertType.Error,
          closeable: true,
        });
      }

      return;
    }

    const obj = this.getReservationBodyObject();

    var delegationId = this.getDelegationId();

    window.scrollTo(0, 0);
    this.reservationService
      .bookReservation(ws.id, ws.floorId, this.selectedTimePeriodId, obj, delegationId)
      .subscribe((resp) => {
        const v = resp?.value as unknown;
        if (
          v != null &&
          typeof v === 'object' &&
          !Array.isArray(v) &&
          (v as Record<string, unknown>)['hasConflict'] === true
        ) {
          const msg = String(
            (v as Record<string, unknown>)['message'] ??
              (resp as { message?: string })?.message ??
              ''
          );
          this.disableReserve = true;
          this.timeConflictMessage = msg;
          this.alertService.setAlert(<AlertInfo>{
            title: 'Unable to book',
            message: msg,
            type: AlertType.Warning,
            closeable: true,
          });
          return;
        }
        if (typeof v === 'number' && v > 0) {
          this.clearAlertOnNewPage = false; // set to false so that the Booking Complete banner doesn't disappear on redirect
          this.router.navigateByUrl('/reservations');
          this.alertService.setAlert(this.buildBookingSuccessAlert(ws.number));
        }
        // refresh list to show ws is unavailable now
      });
  }

  private buildBookingSuccessAlert(deskNumber: string): AlertInfo {
    const isDelegateBooking = this.getDelegationId() != null;
    const title = isDelegateBooking ? 'Delegate booking complete' : 'Booking complete';
    const formatDate = (date: Date | undefined) =>
      this.datePipe.transform(date, 'yyyy-MM-dd') ?? '';

    const message = this.isRepeat
      ? `Desk ${deskNumber} has been successfully reserved from ${formatDate(this.startDate)} to ${formatDate(this.endDate)}.`
      : `Desk ${deskNumber} has been successfully reserved for ${formatDate(this.startDate)}.`;

    return {
      title,
      message,
      type: AlertType.Success,
      closeable: true,
    };
  }

  onResultsPageChanged(event: PageEvent): void {
    let currentScrollY: number =
      window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

    const lastPageIndex: number = Math.max(Math.ceil(event.length / event.pageSize) - 1, 0);
    const previousPageIndex: number = event.previousPageIndex ?? this.tablePageIndex;

    this.tablePageIndex = event.pageIndex;
    this.tablePageSize = event.pageSize;

    // on the last page, the amount of rows is usually less which shifts the page scroll
    // this can result in the user accidentally clicking the favourites button
    // so I added this to prevent that
    if (previousPageIndex === lastPageIndex) {
      currentScrollY = Number.MAX_SAFE_INTEGER;
    }

    this.search(false, true, false);

    const restoreScroll = (): void => {
      window.scrollTo(0, currentScrollY);
      document.documentElement.scrollTop = currentScrollY;
      document.body.scrollTop = currentScrollY;
    };

    requestAnimationFrame(() => {
      restoreScroll();
      requestAnimationFrame(restoreScroll);
    });
  }

  onResultsFavouriteToggle(payload: { row: any; event: Event }): void {
    this.toggleFavourite(payload.row, payload.event);
  }

  onResultsActionButtonClick(payload: { row: any; columnId: string }): void {
    if (payload.columnId === 'actions') {
      this.reserveClicked(payload.row);
    }
  }

  /** True when the given amenity is currently included in the filter */
  isAmenitySelected(amenityId: number): boolean {
    return this.selectedAmenityIds.includes(amenityId);
  }

  /**
   * Adds or removes an amenity from the active filter, then re-filters the
   * already-fetched results and resets to the first page
   */
  onAmenityChipToggle(amenityId: number, selected: boolean): void {
    const nextIds = selected
      ? [...this.selectedAmenityIds, amenityId]
      : this.selectedAmenityIds.filter((id) => id !== amenityId);

    this.selectedAmenityIds = nextIds;
    this.tablePageIndex = 0;
    this.dataSource?.setFeatureFilter(nextIds, this.tablePageSize);
  }

  /** Clears the desk search and immediately restores results matching the other filters */
  clearDeskSearch(): void {
    this.deskSearchControl.setValue('');
    this.deskSearchInput?.nativeElement?.focus();
  }

  private applyDeskSearch(value: string): void {
    this.tablePageIndex = 0;
    this.dataSource?.setDeskSearch(value ?? '', this.tablePageSize);
  }

  private getRowAmenityIcons(row: any): TableIcon[] {
    const attributes = row?.attributes ?? [];
    return attributes.map((amenity: any) => ({
      iconName: getWorkstationAmenityTableIconName(amenity?.workstationAmenity),
      tooltip: getWorkstationAmenityLabel(amenity?.workstationAmenity),
      ariaLabel: getWorkstationAmenityLabel(amenity?.workstationAmenity),
    }));
  }

  getReservationBodyObject(): object {
    const obj: Record<string, Date | number> = {};
    obj['startDate'] = this.startDate;
    if (this.isRepeat) {
      obj['endDate'] = this.endDate;
      obj['Recurrence'] = this.dayMask;
    }

    return obj;
  }

  getDelegationId(): any {
    var delegationId = null;

    if (this.selectedDelegate) {
      if (this.selectedDelegate.id != this.me.id) {
        delegationId = this.selectedDelegate.id;
      }
    }

    return delegationId;
  }

  /** Standardized alert path for recoverable screen-level errors. */
  handleError(
    title: string,
    message: string,
    type: AlertType = AlertType.Error,
    closeable: boolean = true
  ): Observable<any> {
    this.alertService.setAlert(<AlertInfo>{
      title: title,
      message: message,
      type: type,
      closeable: closeable,
    });
    this.search();
    return of();
  }
  /* downloadBuildingProcedure(id: number) {
        this.buildingBpFormControl.setValue(null);
        this.buildingService.downloadProcedure(id).subscribe((data: any) => {
            if (!(data?.value)) {
                return;
            }

            var a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "None";

            var href = "data:attachment/" + data.value?.contentType + ";base64," + data.value?.data;

            a.href = href;
            a.download = data.value?.name;
            a.click();
            a.remove();
        });
    } */

  /** Opens selected building procedure in a new tab. */
  buildingProcedureClicked(val: MatSelectChange['value']) {
    this.buildingBpFormControl.setValue(null);
    var a = document.getElementById(val.buildingProcedureUrl);
    if (a) a.click();
  }

  /** Opens selected floor plan in a new tab. */
  floorPlanClicked(val: MatSelectChange['value']) {
    this.buildingFpFormControl.setValue(null);
    var a = document.getElementById(val.url);
    if (a) a.click();
  }

  /** Returns selected floors as tooltip text for multi-select. */
  legacyGetTooltipContent() {
    let floorContent = 'Floor(s): ';
    const nameList: any[] = [];
    if (this.selectedFloorIds !== undefined && this.selectedFloorIds.length > 0) {
      this.selectedFloorIds.forEach((item) => {
        nameList.push(this.floors.find((x) => x.id == item).name);
      });
    }
    const distinctNames = nameList.filter((n, i) => nameList.indexOf(n) === i);
    let str = distinctNames.join(', ');
    return floorContent + str;
  }

  /** Utility predicate that verifies a Date is populated and parseable. */
  private isValidDate(value: Date): boolean {
    return value instanceof Date && !isNaN(value.getTime());
  }

  /**
   * Returns true when at least one day in the startDate..endDate range
   * matches the dayMask bitmask. Mirrors the backend date-expansion at
   * ReservationService.CheckForTimeConflict / AddBookingBase which does:
   *   Enumerable.Range(0, 1 + endDate - startDate)
   *     .Where(x => ((1 << (int)x.DayOfWeek) & recurrence) > 0)
   * Uses the same 1 << DayOfWeek mapping (Sun=0 .. Sat=6) as .NET.
   */
  private hasOverlappingDays(): boolean {
    if (!this.isValidDate(this.startDate) || !this.isValidDate(this.endDate)) {
      return false;
    }
    const start = new Date(this.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.endDate);
    end.setHours(0, 0, 0, 0);
    const dayCount = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    for (let i = 0; i < dayCount; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      if ((1 << d.getDay()) & this.dayMask) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns the matching WorkstationService.SearchAvailable ValidationException
   * message for the current filter selections, or null when the selections pass.
   * Rule order mirrors the backend so the first-failing banner matches the
   * 400 the user used to see. Strings are character-identical to the backend
   * ValidationException messages so Application Insights cause attribution
   * stays consistent with historical telemetry.
   */
  private validateSearchFilters(): { showBanner: boolean; message: string } | null {
    // Rule 1: startDate must be populated and not in the past.
    // Avoid querying hidden legacy DOM controls; validate using component state.
    if (!this.isValidDate(this.startDate)) {
      return { showBanner: true, message: 'Must select a valid start date' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startAtMidnight = new Date(this.startDate);
    startAtMidnight.setHours(0, 0, 0, 0);
    if (!this.isValidDate(this.startDate) || startAtMidnight < today) {
      return { showBanner: true, message: 'Must select a valid start date' };
    }

    // Rule 2: endDate required when Repeat is ticked. Matches Repro A.
    //   Backend: endDate == null && isRepeat
    if (this.isRepeat && !this.isValidDate(this.endDate)) {
      return { showBanner: true, message: 'Must select a valid end date' };
    }

    const endAtMidnight = new Date(this.endDate);
    endAtMidnight.setHours(0, 0, 0, 0);
    if (this.isRepeat && (!this.isValidDate(this.endDate) || endAtMidnight < today)) {
      return { showBanner: true, message: 'Must select a valid end date' };
    }

    // Rule 3: endDate cannot be before startDate. Matches Repro E.
    //   Backend: endDate.HasValue && endDate.Value < startDate
    if (this.isRepeat && this.isValidDate(this.endDate) && this.endDate < this.startDate) {
      return { showBanner: true, message: 'End date cannot be before start date' };
    }

    // Rule 4: dayMask required whenever endDate has a value.
    //   Backend: endDate.HasValue && (!recurrence.HasValue || recurrence.Value <= 0)
    if (
      this.isRepeat &&
      this.isValidDate(this.endDate) &&
      (this.dayMask == null || this.dayMask <= 0)
    ) {
      return { showBanner: false, message: 'If end date is specified must select days to reserve' }; // NOT SHOWN - Inline validation solves this
    }

    // Rule 4b: dayMask must cover at least one day in startDate..endDate.
    //   Backend: dates.Count == 0 after filtering by recurrence bitmask
    if (
      this.isRepeat &&
      this.isValidDate(this.endDate) &&
      this.dayMask > 0 &&
      !this.hasOverlappingDays()
    ) {
      return { showBanner: true, message: 'The reservation does not contain any valid dates' };
    }

    // Rule 5: timeId must be a real id > 0.
    //   Backend: ReservationTimeSlots.FirstOrDefault(x => x.Id == timeId) == default
    if (this.selectedTimePeriodId == null || this.selectedTimePeriodId <= 0) {
      return { showBanner: false, message: 'Must select a valid time period' }; // NOT SHOWN - Inline validation solves this
    }

    // Rule 6: at least one floor must be selected.
    //   Backend: floorIds == default
    if (this.selectedFloorIds == null || this.selectedFloorIds.length === 0) {
      return { showBanner: false, message: 'Must specify at least one floor to search' }; // NOT SHOWN - Inline validation solves this
    }

    // Rule 7: dates must not be on the weekend
    if (this.isWeekend(this.startDate)) {
      return { showBanner: true, message: 'Start date cannot be on the weekend.' };
    }

    if (this.isRepeat && this.isWeekend(this.endDate)) {
      return { showBanner: true, message: 'End date cannot be on the weekend.' };
    }

    // Rule 8: dates must fall inside the booking window. Matches Repro G.
    //   Backend: startDate.Date > Today.AddDays(maxOpenDays) ||
    //            endDate?.Date > startDate.Date.AddDays(maxOpenDays)
    // `this.maxDate` is the active ceiling maintained by the component
    // (adjusted for OTC same-day rules and super-user via checkOTCTag/getOpeningDays).
    // Booking horizon matches the API for all floors including OTC (28/29 days by time of day).
    if (this.startDate > this.maxDate) {
      return {
        showBanner: true,
        message: `Cannot reserve a workstation more than ${this.getOpeningDays(this.hasSelectedOTCFloor())} days in advance`,
      };
    }

    if (this.isRepeat && this.isValidDate(this.endDate) && this.endDate > this.maxDate) {
      return { showBanner: true, message: this.getBookingWindowExceededMessage() };
    }

    return null;
  }

  private getBookingWindowExceededMessage(): string {
    const maxWindowDays = this.getOpeningDays(this.hasSelectedOTCFloor());
    return `End date cannot be more than ${maxWindowDays} days from today. Please update the end date to search for available desks.`;
  }

  /**
   * Applies only when the user set endDate before selecting startDate.
   * In that flow, endDate must be within the same advance window as the API (28/29 days).
   */
  private validateEndDateWindowWhenSetBeforeStartDate(): string {
    if (!this.endDateEditedBeforeStartDate || !this.isValidDate(this.endDate)) {
      return null;
    }

    const maxWindowDays = this.getOpeningDays(this.hasSelectedOTCFloor());
    const maxEndDateFromToday = new Date();
    maxEndDateFromToday.setHours(0, 0, 0, 0);
    maxEndDateFromToday.setDate(maxEndDateFromToday.getDate() + maxWindowDays);

    const selectedEndDate = new Date(this.endDate);
    selectedEndDate.setHours(0, 0, 0, 0);

    if (selectedEndDate > maxEndDateFromToday) {
      return `End date cannot be more than ${maxWindowDays} days from today. Please update the end date to search for available desks.`;
    }

    return null;
  }

  /** Returns true when any currently selected floor is OTC. */
  private hasSelectedOTCFloor(): boolean {
    if (
      !this.selectedFloorIds ||
      this.selectedFloorIds.length === 0 ||
      !this.floors ||
      this.floors.length === 0
    ) {
      return false;
    }

    return this.selectedFloorIds.some((id) => {
      const floor = this.floors.find((f) => f.id === id);
      return (
        !!floor &&
        (floor.isOTC === true ||
          String(floor.name ?? '')
            .toUpperCase()
            .indexOf('OTC') >= 0)
      );
    });
  }

  /** Utility predicate for day-level today comparison. */
  isToday = (someDate?: Date) => {
    if (someDate == undefined) return false;
    const today = new Date();
    return (
      someDate.getDate() == today.getDate() &&
      someDate.getMonth() == today.getMonth() &&
      someDate.getFullYear() == today.getFullYear()
    );
  };

  /** Returns tomorrow's date or if Friday, the next available date (next Monday). */
  getTomorrow = () => {
    const today: Date = new Date();
    const tomorrow: Date = new Date(today);
    const daysToAdd: 1 | 3 = today.getDay() === 5 ? 3 : 1;
    tomorrow.setDate(today.getDate() + daysToAdd);
    return tomorrow;
  };

  /**
   * Legacy OTC validation path (retained for compatibility).
   * Keep in sync with `checkOTCTag` if business rules change.
   */
  checkOTC() {
    let otcMessage = '';
    let otcId = 0;
    let otc5daysFloor = false;

    this.selectedFloorIds?.forEach((id) => {
      if (this.floors.filter((f) => f.id === id)[0].name.indexOf('OTC') > 0) {
        otcMessage = `The OTC floor ${this.floors.filter((f) => f.id === id)[0].name} can't be booked. Please try to book another workstation.`;
        otcId = id;
        let periodOfDays = this.getOpeningDays(true);

        this.maxDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * periodOfDays);
        this.isRepeatDisable = false;
        // this.isRepeat = true;

        let buildingName = this.buildingsBySelectedUser.filter((b) => b.id === this.buildingId)[0]
          .name;
        if (buildingName.indexOf('5775 Yonge') > -1 || buildingName.indexOf('119 King') > -1) {
          this.homeForm.controls.startDate.patchValue(this.startDate);
          this.maxDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * periodOfDays);
          this.isRepeatDisable = true;
          this.isRepeat = false;
          if (this.startDate > this.maxDate) {
            this.startDate = new Date();
            otcMessage = `Please re-enter the correct start date.`;
          }
        }
      } else {
        this.maxDate = new Date(
          new Date().getTime() + 1000 * 60 * 60 * 24 * this.getOpeningDays(false)
        );
        this.isRepeatDisable = false;
        this.isRepeat = false;
      }
    });

    if (this.isToday(this.startDate) && otcId > 0) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        data: otcMessage,
      });

      dialogRef
        .afterClosed()
        .pipe(first())
        .subscribe((result) => {
          if (result) {
            var index = this.selectedFloorIds.indexOf(otcId);
            if (index !== -1) {
              this.startDate = this.getTomorrow();
              this.selectedFloorIds = [];
              // this.selectedFloorIds.splice(index, 1);
            }

            this.homeForm.controls.selectFloors.patchValue(this.selectedFloorIds);
          }
        });
    } else if (otcId > 0 && this.isRepeat) {
      this.maxDate = new Date(
        new Date().getTime() + 1000 * 60 * 60 * 24 * this.getOpeningDays(true)
      );
    }
  }

  /** Applies OTC floor constraints to date/repeat selection. */
  checkOTCTag(): void {
    let otcMessage: string = '';
    let otcId: number = 0;
    let otc5daysFloor: boolean = false;

    this.selectedFloorIds?.forEach((id) => {
      if (this.floors.filter((f) => f.id === id)[0].isOTC) {
        otcMessage = `The OTC floor ${this.floors.filter((f) => f.id === id)[0].name} can't be booked on the same day. Please try booking for a different date or another floor.`;
        otcId = id;

        let buildingName = this.buildingsBySelectedUser.filter((b) => b.id === this.buildingId)[0]
          .name;

        this.homeForm.controls.startDate.patchValue(this.startDate);

        let periodOfDays = this.getOpeningDays(true);

        this.maxDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * periodOfDays);

        if (this.startDate > this.maxDate) {
          this.startDate = new Date();
          otcMessage = `Start date cannot be more than ${periodOfDays} days from today. Please re-enter the start date.`;
        }
      }
    });

    // If any OTC floor is selected, isRepeat is set to false
    // Also some edge cases to note:
    // - Only Non-OTC selected = isRepeat is unchanged, isRepeatDisable is set to false
    // - Only OTC selected = isRepeat is set to false, isRepeatDisable is set to true
    // - Non-OTC + OTC both selected = isRepeat is set to false, isRepeatDisable is set to true
    if (otcId > 0) {
      this.isRepeatDisable = true;
      this.isRepeat = false;
    } else {
      this.isRepeatDisable = false;

      // No OTC floor selected: maxDate is the standard advance window
      this.maxDate = new Date(
        new Date().getTime() + 1000 * 60 * 60 * 24 * this.getOpeningDays(false)
      );
    }

    if (this.isToday(this.startDate) && otcId > 0) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        data: otcMessage,
      });

      dialogRef
        .afterClosed()
        .pipe(first())
        .subscribe((result) => {
          var index = this.selectedFloorIds.indexOf(otcId);
          if (index !== -1) {
            this.startDate = this.getTomorrow();
            if (this.isRepeat && this.isValidDate(this.endDate)) {
              const shiftedEndDate = new Date(this.endDate);
              shiftedEndDate.setDate(shiftedEndDate.getDate() + 1);
              this.endDate = shiftedEndDate;
            }
            // this.selectedFloorIds = [];
            // this.selectedFloorIds.splice(index, 1);
          }

          // this.homeForm.controls.selectFloors.patchValue(this.selectedFloorIds);
        });
    } else if (otcId > 0 && this.isRepeat) {
      let periodOfDays = this.getOpeningDays(true);
      this.maxDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * periodOfDays);
    }
  }

  /**
   * Based on current date/time, returns advance booking window (days from today).
   * OTC floors are limited to 7 days (or 8 when current hour >= 15).
   * All other floors use 28/29 days.
   * @param isOTC whether the selected floor is an OTC floor.
   */
  getOpeningDays(isOTC?: boolean) {
    var today = new Date();
    let hours = today.getHours();

    // SU gets 1 year opening, OTC or not
    if (this.isSuperUser) return 365;

    if (isOTC) return hours >= 15 ? 8 : 7;

    return hours >= 15 ? 29 : 28;
  }

  /** Authorization helper: Super User role check. */
  authorizeSuperUser(roles: any[]): boolean {
    for (let r of roles) {
      if (r.roleTypeId === RoleConst.SuperUser) {
        return true;
      }
    }
    return false;
  }

  /** Authorization helper: Editor-level role check. */
  authorizeEditor(roles: any[]): boolean {
    for (let r of roles) {
      if (r.roleTypeId === RoleConst.SuperUser || r.roleTypeId === RoleConst.SpaceAdmin) {
        return true;
      }
    }
    return false;
  }

  /** Authorization helper: User Admin role check. */
  authorizeUserAdmin(roles: any[]): boolean {
    for (let r of roles) {
      if (r.roleTypeId === RoleConst.UserAdmin) {
        return true;
      }
    }
    return false;
  }

  /**
   * Applies current user context, role flags, and user-scoped building data.
   */
  private applyUser(user: any | null): void {
    if (!user) {
      return;
    }

    const roles = user.userRoles ?? [];
    this.isSuperUser = this.authorizeSuperUser(roles);
    this.isUserAdmin = this.authorizeUserAdmin(roles);
    this.isEditor = this.authorizeEditor(roles);

    const isSameUser = this.me?.id === user.id;
    if (isSameUser) {
      return;
    }

    this.me = { id: user?.id, firstName: user?.firstName, lastName: user?.lastName };
    this.userId = this.me.id;

    this.searchedUsers = this.searchedUsers.filter((u) => u?.id !== this.me.id);
    this.searchedUsers.push(this.me);
    this.selectedDelegate = this.me;
    this.refreshBookingForOptions();

    // The "Booking for" dropdown is shown when:
    //   - the user is SuperUser/UserAdmin (can book for anyone — type-to-search), OR
    //   - getDelegations('') returns at least one delegator (set in searchForDelegation).
    // Seed the role-based half here; the list-based half resolves when the API call returns.
    this.setHasDelegations(this.isSuperUser || this.isUserAdmin);
    this.searchForDelegation('');

    // Showing buildings related to the logged user and delegated users
    this.buildingsByLoggedUser$ = this.buildingService.getBuildingsByLoggedUser(this.me.id).pipe(
      observeOn(asyncScheduler),
      map((data) => data?.value?.result ?? []),
      tap((buildings) => {
        this.buildingsByLoggedUser = buildings;
        if (buildings.length > 0) {
          this.setBuildingsFloorPlans();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.getBuildingBySelectedUser();
    // Favourites are loaded by the selectedDelegate setter above (it fires both on initial
    // user context — set to self — and on subsequent delegate switches), keeping the list
    // aligned with the booking target without an extra fetch here.
  }

  private setHasDelegations(value: boolean): void {
    if (this.hasDelegations === value) {
      return;
    }

    this.hasDelegations = value;
    this.hasDelegations$.next(value);
    this.refreshBookingForOptions();
  }

  /**
   * Returns the user id whose favourites list is currently in scope: the selected delegate
   * when booking on someone else's behalf, otherwise the logged-in user. Centralizes the
   * rule so `loadFavourites` and `toggleFavourite` always agree on the target.
   */
  private getFavouritesTargetUserId(): number | null {
    return this._selectedDelegate?.id ?? this.me?.id ?? null;
  }

  /**
   * Loads the favourite workstation ids for the active booking target (self or selected
   * delegate). Called on initial page load and whenever the delegate selection changes;
   * the result is then maintained client-side until the next context change.
   */
  loadFavourites(): void {
    const targetUserId = this.getFavouritesTargetUserId();
    if (!targetUserId) {
      return;
    }

    // Stamp the request so a late response from a previous delegate cannot overwrite
    // favourites that now belong to a newly-selected target.
    const requestForUserId = targetUserId;
    this.favouriteService.getFavouriteWorkstations(requestForUserId).subscribe((data) => {
      if (this.getFavouritesTargetUserId() !== requestForUserId) {
        return;
      }
      const incomingFavourites: number[] = data?.value ?? [];
      this.favouriteWorkstationIds = incomingFavourites;
      this.dataSource?.reSortFavourites(this.favouriteWorkstationIds);
    });
  }

  /** True when the workstation id is currently saved as a favourite for the active target. */
  isFavourite(workstationId: number): boolean {
    return this.favouriteWorkstationIds.includes(workstationId);
  }

  /** True while an add/remove request for this workstation is still in flight. */
  isFavouriteRequestPending(workstationId: number): boolean {
    return this.pendingFavouriteWorkstationIds.includes(workstationId);
  }

  /**
   * Tooltip + aria-label for the heart icon. While booking for a delegate the message
   * explains why the icon is read-only.
   */
  favouriteIconLabel(workstationId: number): string {
    if (!this.canEditFavourites) {
      return "You can't edit another user's favourites";
    }
    return this.isFavourite(workstationId) ? 'Remove from favourites' : 'Add to favourites';
  }

  /**
   * Adds or removes a workstation from the active booking target's favourites (self or
   * selected delegate). The heart icon flips immediately via the `isFavourite` binding, but
   * the desk's position in the current results is intentionally NOT changed — ranking only
   * refreshes on the next search/page load. If the booking target changes while a request
   * is in flight, the late response is discarded so it cannot pollute the new target's list.
   */
  toggleFavourite(workstation: any, event: Event): void {
    event.stopPropagation();

    // Only the target user themselves may edit their own favourites; the disabled state on
    // the button already prevents this in the UI, but guard here in case the click somehow
    // fires (programmatic invocation, accessibility tools, etc.).
    if (!this.canEditFavourites) {
      return;
    }

    const workstationId: number = workstation.id;
    if (this.isFavouriteRequestPending(workstationId)) {
      return;
    }

    const targetUserId = this.getFavouritesTargetUserId();
    if (!targetUserId) {
      return;
    }

    this.pendingFavouriteWorkstationIds.push(workstationId);
    const isCurrentlyFavourite: boolean = this.isFavourite(workstationId);

    if (isCurrentlyFavourite) {
      const removeSubscription = this.favouriteService
        .removeFavouriteWorkstation(targetUserId, workstationId)
        .subscribe({
          next: () => {
            if (this.getFavouritesTargetUserId() !== targetUserId) {
              return;
            }
            this.favouriteWorkstationIds = this.favouriteWorkstationIds.filter(
              (id) => id !== workstationId
            );
            this.favouritesInResultsCount -= 1;
          },
          error: () => {
            this.alertService.setAlert(<AlertInfo>{
              title: 'Error Removing Favourite',
              message: 'Could not remove workstation from favourites',
              type: AlertType.Error,
              closeable: true,
            });
          },
        });

      removeSubscription.add(() => {
        this.pendingFavouriteWorkstationIds = this.pendingFavouriteWorkstationIds.filter(
          (id) => id !== workstationId
        );
      });
      return;
    }

    const userFavourite = {
      userId: targetUserId,
      workstationId: workstationId,
    };

    const addSubscription = this.favouriteService.addFavouriteWorkstation(userFavourite).subscribe({
      next: () => {
        if (this.getFavouritesTargetUserId() !== targetUserId) {
          return;
        }
        if (!this.favouriteWorkstationIds.includes(workstationId)) {
          this.favouriteWorkstationIds.push(workstationId);
          this.favouritesInResultsCount += 1;
        }
      },
      error: () => {
        this.alertService.setAlert(<AlertInfo>{
          title: 'Error Adding Favourite',
          message: 'Could not add workstation to favourites',
          type: AlertType.Error,
          closeable: true,
        });
      },
    });

    addSubscription.add(() => {
      this.pendingFavouriteWorkstationIds = this.pendingFavouriteWorkstationIds.filter(
        (id) => id !== workstationId
      );
    });
  }

  ngOnDestroy(): void {
    if (this.clearAlertOnNewPage) {
      this.alertService.clearAlert();
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.userSearchSub?.unsubscribe();
  }
}
